import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationsService } from 'angular2-notifications';
import { SharedService } from 'src/app/_shared/shared.service';
import { RepService } from '../../services/rep.service';
import { IDurationOption, IVehicleType } from '../../models/reservation';
import { Station } from '../../models/station';
import { combineDateAndTime, dateOnly, timeOnly, toDate, toServerDateTime } from 'src/app/_shared/date-utils';

@Component({
  selector: 'app-reservation-form',
  templateUrl: './reservation-form.component.html',
  styleUrls: ['./reservation-form.component.scss']
})
export class ReservationFormComponent implements OnInit {
  reservation: {
    id_location: number | null;
    id_station: number | null;
    id_vehicle_type: number | null;
    license_plate: string;
    duration_minutes: number;
    date: Date | null;
    time: Date | null;
    notes: string;
    status: string;
    notify_user: boolean;
  } = {
    id_location: null,
    id_station: null,
    id_vehicle_type: null,
    license_plate: '',
    duration_minutes: 60,
    date: null,
    time: null,
    notes: '',
    status: 'CONFIRMED',
    notify_user: true
  };
  isSaving = false;
  isEdit = false;
  disabled = false;
  reservationId?: number;
  loadedReservation: any = null;

  locations: any[] = [];
  stations: Station[] = [];
  durationOptions: IDurationOption[] = [];
  vehicleTypes: IVehicleType[] = [];
  statusOptions = [
    { value: 'PENDING', label: 'Čeká na potvrzení' },
    { value: 'CONFIRMED', label: 'Potvrzeno' },
    { value: 'CANCELLED', label: 'Zrušeno' },
    { value: 'COMPLETED', label: 'Dokončeno' }
  ];

  /** True pokud má aktuálně přihlášený uživatel právo spravovat cizí rezervace (r_close). */
  get isAdmin(): boolean {
    return !!this.shared.rights?.r_close;
  }

  calculatedEndTime?: Date;
  estimatedEnergyKwh?: number;
  availabilityChecked = false;
  isAvailable = true;

  minDate: Date;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private repService: RepService,
    private notifications: NotificationsService,
    public shared: SharedService
  ) {
    this.minDate = dateOnly(new Date())!;
  }

  ngOnInit(): void {
    this.loadOptions();

    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.reservationId = parseInt(id, 10);
      this.isEdit = true;
      this.loadReservation();
    }
  }

  /** Po načtení lokací nastav id_station z queryParam (přesměrování z mapy stanic). */
  private applyStationQueryParam(): void {
    const qpStation = this.route.snapshot.queryParamMap.get('id_station');
    if (!qpStation || this.isEdit) return;
    const idStation = parseInt(qpStation, 10);
    for (const loc of this.locations) {
      const found = (loc.stations || []).find((s: any) => s.id_station === idStation);
      if (found) {
        this.reservation.id_location = loc.id_location;
        this.onLocationChange(loc.id_location);
        this.reservation.id_station = idStation;
        this.refreshMapPoints();
        break;
      }
    }
  }

  async loadOptions(): Promise<void> {
    try {
      const [locations, durationOptions, vehicleTypes] = await Promise.all([
        this.repService.getLocations(),
        this.repService.getDurationOptions(),
        this.repService.getVehicleTypes()
      ]);

      this.locations = locations || [];
      this.durationOptions = durationOptions || this.getDefaultDurationOptions();
      this.vehicleTypes = vehicleTypes || [];

      if (!this.reservation.id_vehicle_type && this.vehicleTypes.length) {
        this.reservation.id_vehicle_type = this.vehicleTypes[0].id_vehicle_type;
      }
      this.applyStationQueryParam();
      this.recalcEstimate();
    } catch (error) {
      this.shared.reportError(error, 'Error loading options');
      this.durationOptions = this.getDefaultDurationOptions();
    }
  }

  /** Fallback (30 min až 12 h po 30 min), kdyby BE selhal */
  getDefaultDurationOptions(): IDurationOption[] {
    const options: IDurationOption[] = [];
    for (let m = 30; m <= 12 * 60; m += 30) {
      const h = Math.floor(m / 60);
      const rem = m % 60;
      let label = h > 0 ? `${h} h` : '';
      if (rem > 0) label += (h > 0 ? ' ' : '') + `${rem} min`;
      options.push({ duration_minutes: m, label: label.trim() });
    }
    return options;
  }

  async loadReservation(): Promise<void> {
    try {
      const data = await this.repService.getReservation(this.reservationId!);
      this.loadedReservation = data;
      // Klient nesmí editovat zrušenou/dokončenou. Admin (r_close) ano.
      const status = data.status;
      this.disabled = !this.isAdmin && (status === 'CANCELLED' || status === 'COMPLETED');

      if (data.id_location) {
        this.onLocationChange(data.id_location);
      }

      const dateFrom = toDate(data.date_from as any);
      this.reservation = {
        id_location: data.id_location,
        id_station: data.id_station,
        id_vehicle_type: data.id_vehicle_type,
        license_plate: data.license_plate,
        duration_minutes: data.duration_minutes,
        date: dateOnly(dateFrom),
        time: timeOnly(dateFrom),
        notes: data.notes || '',
        status: status || 'CONFIRMED',
        notify_user: true
      };

      this.calculateEndTime();
      this.recalcEstimate();
      this.refreshMapPoints();
    } catch (error) {
      this.notifications.error('Chyba', 'Nepodařilo se načíst rezervaci');
      this.shared.reportError(error, 'Error loading reservation');
    }
  }

  onLocationChange(locationId: number): void {
    this.reservation.id_station = null;
    const loc = locationId ? this.locations.find(l => l.id_location === locationId) : null;
    this.stations = Station.fromArray(loc?.stations || []);
  }

  /** Body pro Leaflet mapu — stabilní reference, refresh jen při změně stanice. */
  selectedStationMapPoints: any[] = [];

  private refreshMapPoints(): void {
    const stId = this.reservation.id_station;
    const st: any = stId ? this.stations.find(s => s.id_station === stId) : null;
    if (st && st.latitude != null && st.longitude != null) {
      this.selectedStationMapPoints = [{
        id_station: st.id_station, name: st.name,
        latitude: +st.latitude, longitude: +st.longitude
      }];
    } else if (this.selectedStationMapPoints.length) {
      this.selectedStationMapPoints = [];
    }
  }

  onFormChange(): void {
    this.calculateEndTime();
    this.recalcEstimate();
    this.refreshMapPoints();
    this.availabilityChecked = false;
  }

  calculateEndTime(): void {
    const dateFrom = combineDateAndTime(this.reservation.date, this.reservation.time);
    if (dateFrom && this.reservation.duration_minutes) {
      this.calculatedEndTime = new Date(dateFrom.getTime() + this.reservation.duration_minutes * 60000);
    } else {
      this.calculatedEndTime = undefined;
    }
  }

  /** Odhad nabité energie = (duration_minutes / 60) × avg_charging_power_kw vybraného typu vozidla */
  recalcEstimate(): void {
    const r = this.reservation;
    const type = this.vehicleTypes.find(v => v.id_vehicle_type === r.id_vehicle_type);
    if (type && r.duration_minutes) {
      this.estimatedEnergyKwh = Math.round((r.duration_minutes / 60) * type.avg_charging_power_kw);
    } else {
      this.estimatedEnergyKwh = undefined;
    }
  }

  async checkAvailability(): Promise<void> {
    const r = this.reservation;
    const dateFrom = combineDateAndTime(r.date, r.time);
    if (!r.id_location || !r.id_station || !dateFrom || !r.duration_minutes) {
      this.notifications.warn('Upozornění', 'Vyplňte všechna povinná pole');
      return;
    }

    try {
      const result = await this.repService.checkAvailability({
        id_location: r.id_location,
        id_station: r.id_station,
        date_from: toServerDateTime(dateFrom)!,
        duration_minutes: r.duration_minutes,
        id_reservation: this.reservationId
      });

      this.availabilityChecked = true;
      this.isAvailable = result.available;

      if (result.available) {
        this.notifications.success('Dostupné', 'Termín je volný');
      } else {
        this.notifications.error('Nedostupné', 'V požadovaném čase již existuje jiná rezervace');
      }
    } catch (error) {
      this.notifications.error('Chyba', 'Nepodařilo se ověřit dostupnost');
    }
  }

  async onSubmit(): Promise<void> {
    this.isSaving = true;

    try {
      const r = this.reservation;
      const dateFrom = combineDateAndTime(r.date, r.time);
      if (!dateFrom) {
        this.notifications.warn('Upozornění', 'Vyplňte datum a čas');
        return;
      }

      const reservationData: any = {
        id_location: r.id_location,
        id_station: r.id_station,
        id_vehicle_type: r.id_vehicle_type,
        license_plate: r.license_plate.toUpperCase().replace(/\s/g, ''),
        duration_minutes: r.duration_minutes,
        date_from: toServerDateTime(dateFrom),
        notes: r.notes
      };

      // Admin smí editovat status a posílat notifikaci uživateli.
      if (this.isAdmin) {
        reservationData.status = r.status;
        reservationData.notify_user = !!r.notify_user;
      }

      if (this.reservationId) {
        reservationData.id_reservation = this.reservationId;
      }

      // HttpPlus.post interně catchuje chyby a vrací undefined — bez tohoto guardu
      // by se po BE 4xx zobrazil "Úspěch", i když nic neproběhlo.
      const saved = await this.repService.saveReservation(reservationData);
      if (!saved) return;
      this.notifications.success('Úspěch', this.isEdit ? 'Rezervace byla upravena' : 'Rezervace byla vytvořena');
      this.router.navigate([this.isAdmin ? '/admin/reservations' : '/reservations']);
    } catch (error: any) {
      const message = error?.error?.message || 'Nepodařilo se uložit rezervaci';
      this.notifications.error('Chyba', message);
    } finally {
      this.isSaving = false;
    }
  }

  async cancelReservation(): Promise<void> {
    if (!this.reservationId) return;

    if (!confirm('Opravdu chcete zrušit tuto rezervaci?')) return;

    try {
      const opts = this.isAdmin ? { notify_user: !!this.reservation.notify_user } : undefined;
      await this.repService.cancelReservation(this.reservationId, opts);
      this.notifications.success('Úspěch', 'Rezervace byla zrušena');
      this.router.navigate([this.isAdmin ? '/admin/reservations' : '/reservations']);
    } catch (error) {
      this.notifications.error('Chyba', 'Nepodařilo se zrušit rezervaci');
    }
  }

  goBack(): void {
    this.router.navigate(['/reservations']);
  }
}
